import React from "react";
import {
  Box,
  Stack,
  Paper,
  Group,
  Avatar,
  Text,
  TextInput,
  Button,
  ActionIcon,
  Card,
  Grid,
} from "@mantine/core";
import {
  IconSend,
  IconPlus,
  IconArrowLeft,
  IconDotsVertical,
  IconMessage,
  IconChevronRight,
  IconRobot,
  IconMessageChatbot,
  IconSparkles,
  IconGhost,
} from "@tabler/icons-react";

export default function VoiceCentricLayout({
  messages,
  inputValue,
  setInputValue,
  handleSend,
  handleItemSelect,
  previousChats,
  messagesEndRef,
  currentStep,
  config,
  activeTab,
  setActiveTab,
}: any) {
  const theme = config?.theme || {};
  const messaging = config?.messaging || {};

  const renderBotIcon = (size = 28) => {
    const iconType = config?.botAvatarIcon || "robot";
    const iconColor = config?.botAvatarIconColor || "#ffffff";
    switch (iconType) {
      case "chatbot":
        return <IconMessageChatbot size={size} color={iconColor} />;
      case "sparkles":
        return <IconSparkles size={size} color={iconColor} />;
      case "ghost":
        return <IconGhost size={size} color={iconColor} />;
      default:
        return <IconRobot size={size} color={iconColor} />;
    }
  };

  return (
    <Box
      style={{
        backgroundColor: theme.backgroundColor || "#f8f9fa",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Floating Header */}
      <Box p="md">
        <Group
          position="apart"
          p="sm"
          style={{
            backgroundColor: "#fff",
            borderRadius: "50px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
          }}
        >
          <Group spacing="sm">
            {config?.companyLogoUrl && (
              <Avatar src={config.companyLogoUrl} size="md" radius="xl" />
            )}
            <Box>
              <Text weight={700} size="sm">
                {config?.companyName || "Bench AI"} ✨
              </Text>
              <Text size="xs" color="dimmed">
                Always here to help!
              </Text>
            </Box>
          </Group>
          <ActionIcon 
            variant="light" 
            color="blue" 
            radius="xl"
            onClick={() => setActiveTab(activeTab === "current" ? "history" : "current")}
          >
            {activeTab === "current" ? <IconDotsVertical size={20} /> : <IconChevronRight size={20} />}
          </ActionIcon>
        </Group>
      </Box>

      {/* Chat Body */}
      <Stack p="md" style={{ flex: 1, overflowY: "auto" }} spacing="xl">
        {activeTab === "history" ? (
          <Box>
            <Text weight={700} size="sm" mb="md" align="center">Past Conversations</Text>
            {previousChats && previousChats.length > 0 ? (
              <Stack spacing="md">
                {previousChats.map((chat: any, idx: number) => (
                  <Card
                    key={idx}
                    radius="xl"
                    p="md"
                    style={{ backgroundColor: "#fff", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}
                  >
                    <Group position="apart" mb="xs">
                      <Text size="xs" weight={700}>
                        {new Date(chat.created_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Text size="xs" color="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                      {chat.description}
                    </Text>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Text size="xs" color="dimmed" align="center">No past conversations found for this number.</Text>
            )}
          </Box>
        ) : (
          <Box>
            {/* Dynamic Messages */}
        {messages.map((msg: any) => (
          <Box key={msg.id}>
            {msg.sender === "user" ? (
              <Paper
                p="sm"
                radius="md"
                style={{
                  backgroundColor: messaging.userMessageBgColor || "#000",
                  color: messaging.userMessageTextColor || "#fff",
                  marginLeft: "auto",
                  maxWidth: "85%",
                  width: "fit-content",
                  marginTop: 8,
                  marginBottom: 8,
                }}
              >
                <Text size="xs">{msg.text}</Text>
              </Paper>
            ) : (
              <Group align="flex-end" spacing="sm">
                <Avatar
                  radius="xl"
                  size="sm"
                  src={undefined}
                  style={{
                    background:
                      config?.botAvatarBgColor ||
                      "linear-gradient(135deg, #111 0%, #333 100%)",
                  }}
                >
                  {renderBotIcon(16)}
                </Avatar>
                <Box style={{ width: "80%" }}>
                  <Paper
                    p="sm"
                    radius="xl"
                    style={{
                      backgroundColor: messaging.botMessageBgColor || "#ffffff",
                      color: messaging.botMessageTextColor || "#000000",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                      borderBottomLeftRadius: 4, // tiny tail
                    }}
                  >
                    <Text size="xs">{msg.text}</Text>
                  </Paper>
                </Box>
                {/* Bubble Options */}
                {msg.options && (
                  <Group mt="md" spacing="xs" position="center">
                    {msg.options.map((opt: string) => (
                      <Button
                        key={opt}
                        radius="xl"
                        variant="filled"
                        size="sm"
                        style={{
                          backgroundColor: theme.primaryColor || "#339af0",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          height: "auto",
                          minHeight: "36px",
                          whiteSpace: "normal",
                          padding: "10px 16px",
                          lineHeight: 1.4,
                          maxWidth: "100%",
                        }}
                        onClick={() => handleSend(opt)}
                      >
                        {opt}
                      </Button>
                    ))}
                  </Group>
                )}

                {/* List Items */}
                {msg.listItems && (
                  <Stack mt="sm">
                    {msg.listItems.map((item: any, idx: number) => (
                      <Card
                        key={idx}
                        withBorder
                        radius="md"
                        p="sm"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleItemSelect(item)}
                      >
                        <Text weight={600} size="sm">
                          {item.title}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {item.subtitle}
                        </Text>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Group>
            )}
          </Box>
        ))}

        {/* Recent Chats Section - Only show at start */}
        {messages.length <= 1 && previousChats && previousChats.length > 0 && (
          <Box>
            <Group position="apart" mb="xs">
              <Text weight={700} size="xs">
                Recent Chats
              </Text>
              <Text size="xs" color="blue" style={{ cursor: "pointer" }}>
                See All
              </Text>
            </Group>
            <Stack spacing="xs">
              {previousChats.slice(0, 2).map((chat: any) => (
                <Group spacing="sm" key={chat.id} style={{ cursor: "pointer" }}>
                  <Avatar radius="xl" size="sm" color="gray">
                    <IconMessage size={14} />
                  </Avatar>
                  <Box>
                    <Text size="xs" weight={500}>
                      {chat.description?.split("---")[0] || "Chat Record"}
                    </Text>
                    <Text size="10px" color="dimmed">
                      {new Date(chat.created_at).toLocaleString()}
                    </Text>
                  </Box>
                </Group>
              ))}
            </Stack>
          </Box>
        )}
        </Box>
        )}
        <div ref={messagesEndRef} />
      </Stack>

      {/* Floating Footer Input */}
      {activeTab === "current" && (currentStep === "ASK_CONTACT" ||
        currentStep === "ASK_ANYTHING_ELSE") && (
        <Box p="md">
          <Group
            p="xs"
            style={{
              backgroundColor: "#fff",
              borderRadius: "50px",
              boxShadow: "0 -4px 15px rgba(0,0,0,0.05)",
            }}
          >
            <TextInput
              placeholder="Type a message..."
              radius="xl"
              style={{ flex: 1 }}
              value={inputValue}
              onChange={(e) => setInputValue(e.currentTarget.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              variant="unstyled"
              pl="sm"
              rightSection={
                <ActionIcon
                  onClick={() => handleSend()}
                  variant="filled"
                  color="dark"
                  radius="xl"
                  size="lg"
                  style={{ backgroundColor: theme.primaryColor || "#000" }}
                >
                  <IconSend size={16} />
                </ActionIcon>
              }
            />
          </Group>
        </Box>
      )}
    </Box>
  );
}
